/**
 * IDE Format Definitions
 *
 * Defines the configuration for all supported IDE formats
 * including file paths, extensions, and metadata formats.
 *
 * IMPORTANT: Keep in sync with:
 *   - docs/references/IDE-STANDARDS.md (Appendix F: IDE Configuration Registry)
 *   - src/constants/ide-migration-configs.ts (Migration configs)
 */

export interface IdeFormat {
    id: string;
    name: string;
    description: string;
    rulesPath: string;
    workflowsPath?: string;
    fileExtension: string;
    workflowsFileExtension?: string; // Extension for workflows/commands (defaults to fileExtension)
    metadataFormat: 'yaml-frontmatter' | 'none';
    supportsReference: boolean;
    dependencies?: string[];
}

export const IDE_FORMATS: Record<string, IdeFormat> = {
    cursor: {
        id: 'cursor',
        name: 'Cursor',
        description: 'Cursor IDE (.cursor/rules/)',
        rulesPath: '.cursor/rules',
        workflowsPath: '.cursor/commands',
        fileExtension: '.mdc',
        workflowsFileExtension: '.md', // Commands use .md, not .mdc
        metadataFormat: 'yaml-frontmatter',
        supportsReference: true,
    },

    windsurf: {
        id: 'windsurf',
        name: 'Windsurf',
        description: 'Windsurf IDE (.windsurf/rules/)',
        rulesPath: '.windsurf/rules',
        workflowsPath: '.windsurf/workflows',
        fileExtension: '.md',
        metadataFormat: 'yaml-frontmatter',
        supportsReference: true,
    },

    antigravity: {
        id: 'antigravity',
        name: 'Antigravity',
        description: 'Antigravity IDE (.agent/rules/)',
        rulesPath: '.agent/rules',
        workflowsPath: '.agent/workflows',
        fileExtension: '.md',
        metadataFormat: 'yaml-frontmatter',
        supportsReference: true,
    },

    claude: {
        id: 'claude',
        name: 'Claude Code',
        description: 'Claude Code (.claude/rules/)',
        rulesPath: '.claude/rules',
        workflowsPath: '.claude/commands',
        fileExtension: '.md',
        metadataFormat: 'yaml-frontmatter',
        supportsReference: true,
    },

    agentsmd: {
        id: 'agentsmd',
        name: 'AGENTS.md',
        description: 'Single AGENTS.md file',
        rulesPath: '.',
        fileExtension: '.md',
        metadataFormat: 'none',
        supportsReference: false,
    },

    gemini: {
        id: 'gemini',
        name: 'Gemini CLI',
        description: 'Gemini CLI (GEMINI.md)',
        rulesPath: '.',
        fileExtension: '.md',
        metadataFormat: 'none',
        supportsReference: false,
        dependencies: ['agentsmd'], // Gemini requires AGENTS.md
    },

    opencode: {
        id: 'opencode',
        name: 'OpenCode',
        description: 'OpenCode (AGENTS.md + .opencode/command/)',
        rulesPath: '.', // Uses AGENTS.md
        workflowsPath: '.opencode/command',
        fileExtension: '.md',
        metadataFormat: 'yaml-frontmatter',
        supportsReference: true,
        dependencies: ['agentsmd'], // OpenCode requires AGENTS.md for rules
    },

    codex: {
        id: 'codex',
        name: 'Codex',
        description: 'OpenAI Codex (AGENTS.md + .agents/skills/ + .codex/config.toml)',
        rulesPath: '.',
        workflowsPath: '.agents/skills',
        fileExtension: '.md',
        metadataFormat: 'none',
        supportsReference: false,
    },
};

/**
 * Base jai1 rule content that is included in all outputs
 */
export const JAI1_BASE_RULE = `# Jai1 Framework Agent

You are an AI coding assistant integrated with the Jai1 Framework.

## Skills Reference

The Jai1 Framework provides specialized skills that you can reference and apply:

### Pattern Detection

When you encounter:
- **Component creation**: Reference \`@jai1-component-patterns\`
- **API development**: Reference \`@jai1-api-patterns\`
- **Database operations**: Reference \`@jai1-database-patterns\`
- **Testing**: Reference \`@jai1-testing-patterns\`
- **Deployment**: Reference \`@jai1-deployment-patterns\`

### Skill Loading Procedure

1. **Detect the task type** from user request
2. **Load relevant skill** using \`@jai1-[skill-name]\` syntax
3. **Apply patterns** from the skill to your response
4. **Follow conventions** defined in the skill

Example:
\`\`\`
User: "Create a new API endpoint for users"
Assistant:
1. Loading @jai1-api-patterns
2. Applying RESTful conventions
3. Following project structure from @jai1-component-patterns
4. Implementing validation using @jai1-testing-patterns
\`\`\`

### Application

- **Always reference skills** before implementing patterns
- **Follow naming conventions** from loaded skills
- **Apply best practices** defined in skills
- **Maintain consistency** with existing codebase patterns

### Naming Convention

Skills are named using the pattern: \`@jai1-[domain]-patterns\`

Examples:
- \`@jai1-component-patterns\`
- \`@jai1-api-patterns\`
- \`@jai1-database-patterns\`
- \`@jai1-testing-patterns\`
- \`@jai1-deployment-patterns\`

## Best Practices

1. **Load before applying**: Always reference the appropriate skill before implementing
2. **Follow project structure**: Use patterns from \`@jai1-component-patterns\`
3. **Maintain consistency**: Apply conventions consistently across the codebase
4. **Document decisions**: Explain why specific patterns are used

## Integration

This base rule works in conjunction with:
- Project-specific rules (e.g., 01-project.md)
- Technology-specific rules (e.g., 03-frontend.md)
- Custom rules (e.g., 09-custom.md)
- AGENTS.md (if present)

When conflicts arise, prioritize:
1. Custom rules (09-custom.*)
2. Project rules (01-project.*)
3. This base rule (00-jai1.*)
`;

/**
 * Get IDE format by ID
 */
export function getIdeFormat(id: string): IdeFormat | undefined {
    return IDE_FORMATS[id];
}

/**
 * Get all IDE format IDs
 */
export function getIdeFormatIds(): string[] {
    return Object.keys(IDE_FORMATS);
}

/**
 * Validate IDE format ID
 */
export function isValidIdeFormat(id: string): boolean {
    return id in IDE_FORMATS;
}

/**
 * Get IDE formats that require dependencies
 */
export function getIdeFormatsWithDependencies(): string[] {
    return Object.entries(IDE_FORMATS)
        .filter(([_, format]) => format.dependencies && format.dependencies.length > 0)
        .map(([id]) => id);
}
